import type { ElectionStats } from '../types';
import { formatDuration, nonVoters, pct } from './stats';
import { RESULT_LABEL } from './rounds';

const CHANNELS = { KIOSK: 'Kiosco', PUBLIC_LINK: 'Teléfono', ACCOUNT: 'Cuenta' } as const;

/** Statistics of an election to .xlsx (two sheets: summary and one row per round) */
export async function exportStatsExcel(electionName: string, stats: ElectionStats): Promise<void> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();

  const summary = wb.addWorksheet('Resumen');
  summary.columns = [{ header: 'Dato', key: 'k', width: 34 }, { header: 'Valor', key: 'v', width: 18 }];
  const e = stats.election;
  [
    ['Elección', electionName],
    ['Cargos cubiertos', `${e.positions_filled} de ${e.positions}`],
    ['Rondas (cerradas)', `${e.rounds} (${e.rounds_closed})`],
    ['Personas que votaron', e.distinct_voters],
    ['Votos emitidos', e.votes],
    ['Ministros', e.by_kind.MINISTER ?? 0],
    ['Invitados', e.by_kind.GUEST ?? 0],
    ['Votantes manuales', e.by_kind.MANUAL ?? 0],
    ['Votos en el kiosco', e.by_channel.KIOSK ?? 0],
    ['Votos desde el teléfono', e.by_channel.PUBLIC_LINK ?? 0],
    ['Votos con cuenta', e.by_channel.ACCOUNT ?? 0],
    ['Votos corregidos', e.overrides],
  ].forEach(([k, v]) => summary.addRow({ k, v }));
  summary.getRow(1).font = { bold: true };

  const ws = wb.addWorksheet('Rondas');
  ws.columns = [
    { header: 'Cargo', key: 'pos', width: 22 },
    { header: 'Ronda', key: 'round', width: 8 },
    { header: 'Estado', key: 'status', width: 14 },
    { header: 'Habilitados', key: 'eligible', width: 12 },
    { header: 'Votaron', key: 'cast', width: 10 },
    { header: 'Participación %', key: 'turnout', width: 15 },
    { header: 'Válidos', key: 'valid', width: 10 },
    { header: 'En blanco', key: 'blank', width: 10 },
    { header: 'No votaron', key: 'non', width: 11 },
    { header: CHANNELS.KIOSK, key: 'kiosk', width: 10 },
    { header: CHANNELS.PUBLIC_LINK, key: 'phone', width: 10 },
    { header: CHANNELS.ACCOUNT, key: 'account', width: 10 },
    { header: 'Corregidos', key: 'ov', width: 11 },
    { header: 'Duración', key: 'dur', width: 14 },
    { header: 'Cierre', key: 'close', width: 12 },
    { header: 'Abierta', key: 'opened', width: 20 },
    { header: 'Cerrada', key: 'closed', width: 20 },
  ];
  ws.getRow(1).font = { bold: true };
  ws.views = [{ state: 'frozen', ySplit: 1 }];
  const dt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-BO') : '');
  for (const r of stats.rounds) {
    ws.addRow({
      pos: r.position_title ?? '',
      round: r.round_number,
      status: r.status === 'CLOSED' ? (r.result ? RESULT_LABEL[r.result] : 'Cerrada') : r.status === 'OPEN' ? 'Abierta' : 'Borrador',
      eligible: r.eligible ?? '',
      cast: r.votes_cast,
      turnout: pct(r.votes_cast, r.eligible) ?? '',
      valid: r.valid,
      blank: r.blank,
      non: nonVoters(r),
      kiosk: r.by_channel.KIOSK ?? 0,
      phone: r.by_channel.PUBLIC_LINK ?? 0,
      account: r.by_channel.ACCOUNT ?? 0,
      ov: r.overrides,
      dur: formatDuration(r.duration_seconds),
      close: r.close_reason === 'TIMER' ? 'Por tiempo' : r.close_reason === 'MANUAL' ? 'Manual' : '',
      opened: dt(r.opened_at),
      closed: dt(r.closed_at),
    });
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `estadisticas_${electionName.replace(/[^\w-]+/g, '_')}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
